<template lang="html">
  <section :id="translationId" class="custom-display-element-edit-translation">
    <div v-if="isEdit" class="edit-translation">
      <div class="input-box-container">
        <div class="typeahead-container">
          <type-ahead
            :id="inputLocaleId"
            :list="options"
            :selected-list="selectedOptions"
            property="locale"
            :value="localeValue"
            @on-typeahead-change="updateSelectedOptions"
            @on-typeahead-selected="updateSelectedOptions"
          />
        </div>
        <span>:</span>
        <input
          :id="inputLabelId"
          class="text-input"
          v-model.trim="editTranslation.label"
          :maxlength="INPUT_ELEMENT.TRANSLATION_LABEL_MAX_CHARS"
          @keyup="toggleOK"
          autocomplete="off"
        />
      </div>
      <div class="edit-buttons">
        <button class="m-button outline-brand" @click="onCancel">
          {{ getMessageText(messages.CANCEL) }}
        </button>
        <button class="m-button brand" @click="onSave" :disabled="disableSave">
          {{ getMessageText(messages.SAVE) }}
        </button>
      </div>
    </div>

    <div v-else class="translation-info">
      <div class="truncate">{{ updateLocale }} :</div>
      <div class="truncate">{{ updateLabel }}</div>
      <div v-if="saving" class="loader"></div>
      <div v-else class="edit" @click="onEditTranslations">
        <svg class="edit-icon">
          <use
            xlink:href="@/assets/icons/utility-sprite/svg/symbols.svg#edit"
          />
        </svg>
      </div>
    </div>

    <div
      class="delete"
      tabindex="0"
      @keydown.enter.stop="onDeleteTranslations"
      @click="onDeleteTranslations"
    >
      <svg class="delete-icon">
        <use
          xlink:href="@/assets/icons/utility-sprite/svg/symbols.svg#delete"
        />
      </svg>
    </div>

    <simple-modal
      :show="displaySimpleModal"
      :message="getMessageText(messages.ARE_YOU_SURE)"
      :cancel-text="getMessageText(messages.CANCEL)"
      :ok-text="getMessageText(messages.OK)"
      @on-simple-modal-cancel="onCancelDeleteElement"
      @on-simple-modal-ok="onConfirmDeleteElement"
    />
  </section>
</template>

<script lang="js">
  import { TOAST_OPTIONS, INPUT_ELEMENT } from "@/static/global-variables";
  import LocaleUtil from "@/utils/locales-util";
  import TypeAhead from "@/components/TypeAhead";
  import DistributionChannelsService from "@/services/distributionChannelsService";
  import SimpleModal from "@/views/drag-drop-pages/components/modal/SimpleModal";

  export default  {
    name: "custom-display-element-edit-translation",
    components: {
      SimpleModal,
      TypeAhead
    },
    props: {
      id: String,
      translation: Object,
      widgetuuid: String,
      options: Array
    },
    data () {
      return {
        INPUT_ELEMENT,
        isEdit: false,
        saving: false,
        displaySimpleModal: false,
        disableSave: false,
        selectedOptions: []
      }
    },
    computed: {
      translationId() {
        return this.id || `edit-translation-${this.translation.uuid}`;
      },
      inputLocaleId() {
        return `language-${this.translation.uuid}`;
      },
      inputLabelId() {
        return `label-${this.translation.uuid}`;
      },
      messages() {
        return this.$store.state.messages;
      },
      editTranslation() {
        return {...this.translation};
      },
      updateLocale() {
        return this.saving? this.editTranslation.language : this.translation.language;
      },
      updateLabel() {
        return this.saving? this.editTranslation.label : this.translation.label;
      },
      localeValue() {
        return this.options.find(e => e.locale === this.editTranslation.language);
      }
    },
    mounted() {
      this.updateSelectedOptions(this.inputLocaleId, this.localeValue);
    },
    methods: {
      updateSelectedOptions(id, item) {
        if(item) {
          this.editTranslation.language = item.locale;
        } else {
          this.editTranslation.language = "";
        }
        LocaleUtil.addWidgetTranslationLocale(this.widgetuuid, id, item);
        this.selectedOptions = LocaleUtil.getSelectedLocales();
        this.toggleOK();
      },
      onEditTranslations() {
        this.isEdit = true;
      },
      onDeleteTranslations() {
        if(!this.saving) {
          this.displaySimpleModal = true;
        }
      },
      onCancel() {
        this.isEdit = false;
        this.reset();
      },
      onSave() {
        if(this.allowUpdate()) {
          this.saving = true;
          this.isEdit = false;
          DistributionChannelsService.updateTranslation(this.editTranslation)
          .catch((e) => {
            this.$bvToast.toast(this.getMessageText(this.messages.SAVE_ERROR), TOAST_OPTIONS.bottomCenter);
            console.error(e);
          })
          .finally(() => {
            this.saving = false;
            this.reset();
          });
        } else {
          this.isEdit = false;
          this.reset();
        }
      },
      reset() {
        this.editTranslation.language = this.translation.language;
        this.editTranslation.label = this.translation.label;
      },
      allowUpdate() {
        return !this.isEmptyInput() && this.hasUpdatedTranslation();
      },
      isEmptyInput() {
        return !this.editTranslation.language || !this.editTranslation.label;
      },
      hasUpdatedTranslation() {
        return this.editTranslation.language !== this.translation.language ||
              this.editTranslation.label !== this.translation.label;
      },
      toggleOK() {
        this.disableSave = this.isEmptyInput();
      },
      onCancelDeleteElement() {
        this.displaySimpleModal = false;
      },
      onConfirmDeleteElement() {
        this.displaySimpleModal = false;
        this.saving = true;
        DistributionChannelsService.deleteWidgetTranslation(this.editTranslation)
        .then(() => {
          this.updateSelectedOptions(this.inputLocaleId);
        })
        .catch((e) => {
          this.$bvToast.toast(this.getMessageText(this.messages.SAVE_ERROR), TOAST_OPTIONS.bottomCenter);
          console.error(e);
        })
        .finally(() => {
          this.saving = false;
        });
      }
    }
  }
</script>

<style scoped lang="scss">
  @import "@/styles/components/main";
  .custom-display-element-edit-translation {
    box-sizing: border-box;
    display: grid;
    grid-template-columns: auto 2em;
    border-bottom: 1px solid var(--LIGHT_GRAY);
    .edit-translation {
      box-sizing: border-box;
      display: grid;
      grid-template-columns: auto 10rem;
      align-items: center;
      padding: 0.2rem 0;
      .input-box-container {
        display: grid;
        grid-template-columns: 5.2rem 0.3rem auto;
        column-gap: 0.4rem;
        align-items: center;
        .typeahead-container {
          position: relative;
        }
        .text-input {
          @include mixin-text-input;
        }
      }
      .edit-buttons {
        display: flex;
        justify-content: space-evenly;
      }
    }

    .translation-info {
      display: grid;
      grid-template-columns: 5.2em auto 2rem;
      padding: 0.6rem 0;
      .edit {
        justify-self: center;
        .edit-icon {
          cursor: pointer;
          height: 0.8rem;
          width: 0.8rem;
          margin-top: 0.2rem;
          fill: var(--DARK_GREY);
        }
      }
      .loader {
        width: 1.22rem;
        height: 1.22rem;
        margin: 0;
      }
    }
    .delete {
      justify-self: center;
      align-self: center;
      .delete-icon {
        cursor: pointer;
        height: 1rem;
        width: 1rem;
        fill: var(--SF_ERROR_RED);
      }
    }
  }
</style>
